import type { ArrowData, Cell, Direction, Level } from './types';

/**
 * A level in a compact, exact form for saves: every cell is two characters (x, y) of a
 * 64-letter alphabet, in the original order. A 28 × 36 board saves in about 4 KB instead
 * of 40 KB of JSON, so in-flight attempts fit the server's state size (hello.maxStateBytes).
 */
export type EncodedLevel = Omit<Level, 'active' | 'arrows'> & {
  active: string;
  /** [id, cells, direction] per arrow. */
  arrows: [string, string, Direction][];
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const DIRECTIONS: readonly Direction[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];

function encodeCells(cells: Cell[]): string {
  return cells.map(({ x, y }) => {
    if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= ALPHABET.length || y >= ALPHABET.length) throw new Error(`cell ${x},${y} cannot be encoded`);
    return ALPHABET[x]! + ALPHABET[y]!;
  }).join('');
}

function decodeCells(text: string): Cell[] {
  if (typeof text !== 'string' || text.length % 2 !== 0) throw new Error('invalid cells');
  const cells: Cell[] = [];
  for (let i = 0; i < text.length; i += 2) {
    const x = ALPHABET.indexOf(text[i]!), y = ALPHABET.indexOf(text[i + 1]!);
    if (x < 0 || y < 0) throw new Error('invalid cells');
    cells.push({ x, y });
  }
  return cells;
}

export function encodeLevel(level: Level): EncodedLevel {
  return {
    ...level,
    active: encodeCells(level.active),
    arrows: level.arrows.map(arrow => [arrow.id, encodeCells(arrow.cells), arrow.direction]),
  };
}

/** Throws on anything that is not an encoded level. */
export function decodeLevel(encoded: EncodedLevel): Level {
  if (!encoded || typeof encoded !== 'object' || !Array.isArray(encoded.arrows)) throw new Error('invalid level');
  const arrows: ArrowData[] = encoded.arrows.map(entry => {
    if (!Array.isArray(entry) || typeof entry[0] !== 'string' || !DIRECTIONS.includes(entry[2])) throw new Error('invalid arrow');
    return { id: entry[0], cells: decodeCells(entry[1]), direction: entry[2] };
  });
  return { ...encoded, active: decodeCells(encoded.active), arrows };
}
